import { CheckCircle2 } from 'lucide-react';
import { buttonClass } from './CheckoutControls';
import { formatCents, type OrderSummary } from '@/lib/checkout/pricing';

interface CheckoutConfirmationProps {
  email: string;
  order: OrderSummary;
  onGoToSignIn: () => void;
}

/** Post-purchase success state — membership is Pending until setup completes. */
export default function CheckoutConfirmation({
  email,
  order,
  onGoToSignIn,
}: CheckoutConfirmationProps) {
  return (
    <div className="text-center">
      <span className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#e8fbf6]">
        <CheckCircle2 aria-hidden="true" className="h-8 w-8 text-[#02b899]" />
      </span>
      <h2 className="m-0 mb-2 text-xl font-bold text-[#4b0a5b] sm:text-[22px]">
        You’re in — welcome to WorldVia
      </h2>
      <p className="mx-auto mb-5 max-w-[52ch] text-[13px] leading-relaxed text-[#7a6a8c]">
        We charged {formatCents(order.dueNowCents)} and sent a confirmation to{' '}
        <strong className="text-[#3d1751]">{email}</strong>. Check your email for next steps.
      </p>

      <dl className="mx-auto mb-6 max-w-[420px] rounded-2xl border border-[#ece6f0] bg-[#fbfafc] p-4 text-left">
        <div className="flex justify-between gap-3 py-1 text-[12.5px]">
          <dt className="text-[#6b5b7d]">Membership status</dt>
          <dd className="rounded-full bg-[#fff4ee] px-2.5 py-0.5 text-[11.5px] font-bold text-[#c2410c]">
            Pending
          </dd>
        </div>
        <div className="flex justify-between gap-3 py-1 text-[12.5px]">
          <dt className="text-[#6b5b7d]">Paid today</dt>
          <dd className="font-semibold text-[#3d1751]">{formatCents(order.dueNowCents)}</dd>
        </div>
        <div className="flex justify-between gap-3 py-1 text-[12.5px]">
          <dt className="text-[#6b5b7d]">Renews monthly at</dt>
          <dd className="font-semibold text-[#3d1751]">{formatCents(order.monthlyCents)}</dd>
        </div>
      </dl>

      <p className="mx-auto mb-5 max-w-[52ch] text-[12.5px] leading-relaxed text-[#7a6a8c]">
        Your membership stays <strong className="text-[#3d1751]">Pending</strong> while we finish
        provisioning your account and review your company documents. We’ll email you the moment it’s
        active.
      </p>

      <div className="flex justify-center">
        <button type="button" onClick={onGoToSignIn} className={`${buttonClass('sm')} px-6`}>
          Sign in to AIVIA
        </button>
      </div>
    </div>
  );
}
